import { Popups } from '../../../providers/popups'
import { prepareDeltaToPost } from '../../../utils'
import { makeRequest, REQUEST_ABORTED } from '../../../request'
import { ComposeState } from './composeTypes'
import { User } from '../../../providers/context'
import { AxiosError } from 'axios'
import { Toasts } from '../../../providers/toast'
import assert from 'assert'
import { PreviewState, singleSelectedRecipient } from './composeHelpers'
import { Languages } from '../../../providers/languages'
import { format, toZonedTime } from 'date-fns-tz'

export const preSendChecks = async (composeState: ComposeState, previewState: PreviewState, user: User) => {
  if (
    (await noRecipientsCheck(previewState)) &&
    (await earlyMessageWarning(composeState, user)) &&
    (await illegalScriptTagsCheck(composeState)) &&
    (await composeLargeMessageWarning(composeState, previewState)) &&
    (await massMessagePopup(composeState)) &&
    (await confirmReceiptWarning(composeState))
  ) {
    const newComposeState = await fillInMissingLanguages(composeState)
    if (newComposeState && (await confirmLKCallCosts(newComposeState))) return newComposeState
  }
  return undefined
}

const earlyMessageWarning = async (composeState: ComposeState, user: User) => {
  if (composeState.sendOption.type !== 'later') return true
  const time = composeState.sendLaterDate!
  const timeZone = user.district.timeZone
  const zonedTime = toZonedTime(time, timeZone)
  const hours = zonedTime.getHours()
  return (
    (hours < 22 && hours >= 5) ||
    !!(await Popups.confirm({
      message: (
        <>
          Are you sure you want to schedule a message to be sent at{' '}
          <b>{format(zonedTime, 'h:mm a zzz', { timeZone })}</b>?
        </>
      ),
      yesText: 'Yes',
      noText: 'Cancel',
    }))
  )
}

const noRecipientsCheck = async (previewState: PreviewState) => {
  if (previewState === undefined || previewState.count === undefined || previewState.count > 0) return true
  // this means that you should be able to preview/send (not undefined), but it would be to 0 people
  await Popups.simplePopup('This message would not go to any recipients.')
  return false
}

const lkLanguageInfoTooltip =
  "Don't see the language you need? Call Lexikeet at 1-844-539-5338 for an interpreter in any language."

const fillInMissingLanguages = async (composeState: ComposeState) => {
  if (composeState.sendOption.type === 'call-interpreted' || composeState.sendOption.type === 'schedule-interpreted') {
    const onDemandOnly = composeState.sendOption.type === 'call-interpreted'
    const languageOptions = Languages.getOptions((l) => (onDemandOnly ? l.lexikeetOnDemand : (l.lexikeetCode || 0) > 0))
    const newLanguage = await Popups.select({
      title: (
        <>
          Please select a language for <br />
          this interpreted call:
        </>
      ),
      options: languageOptions,
      value: singleSelectedRecipient(composeState)!.contactMethods.language,
      yesText: 'Continue',
      cancel: 'Cancel',
      info: lkLanguageInfoTooltip,
    })

    if (newLanguage != undefined)
      return {
        ...composeState,
        lexikeetCallLanguage: Languages.find(newLanguage.value),
      }
    return undefined
  }
  return composeState
}

const confirmLKCallCosts = async (composeState: ComposeState) => {
  const singleRecipient = singleSelectedRecipient(composeState)
  if (!singleRecipient || !composeState.lexikeetCallLanguage) return true

  const extraCount =
    composeState.recipientsInfo.extraInvites.length + composeState.recipientsInfo.sharedTeacherRecipients.length
  const extraInfo = extraCount > 0 ? ' and also invite ' + extraCount + ' other people' : ''

  if (composeState.sendOption.type === 'call-interpreted') {
    return !!(await Popups.confirm({
      message: (
        <>
          Are you sure you want to make a <b>{composeState.lexikeetCallLanguage.name}</b> interpreted call to{' '}
          {singleRecipient.name}
          {extraInfo}? Everyone called will be emailed or texted invite links. <b>This will incur additional costs.</b>
        </>
      ),
      yesText: 'Yes',
      noText: 'No',
    }))
  } else if (composeState.sendOption.type === 'schedule-interpreted') {
    return !!(await Popups.confirm({
      message: (
        <>
          Are you sure you want to schedule a <b>{composeState.lexikeetCallLanguage.name}</b> interpreted call with{' '}
          {singleRecipient.name}
          {extraInfo}?{' '}
          {composeState.callSuggestingTimes ? (
            <>Once the recipient confirms a time, everyone selected will be emailed or texted invite links.</>
          ) : (
            <>Everyone selected will be emailed or texted invite links.</>
          )}{' '}
          <b>This interpreted call will incur additional costs.</b>{' '}
          <b>These costs will not be incurred if you cancel more than 24 hours before the call.</b>
        </>
      ),
      yesText: 'Yes',
      noText: 'No',
    }))
  }
  return true
}

const massMessagePopup = async (composeState: ComposeState) => {
  if (!composeState.sendTypes.has('mass') || composeState.sendOption.type === 'draft') return true
  return !!(await Popups.simpleConfirm(
    <b>Are you sure you want to send this broadcast message?</b>,
    <ul className="list-inside list-disc">
      <li>
        Verify the text content of the message - users that we can't reach by phone will receive the message as an
        email.
      </li>
      <li>Recording - we recommend saying the name of the district/school first to clarify who the message is from.</li>
      <li>Test it out - you can send a test to yourself now to see how it sounds.</li>
    </ul>
  ))
}

const illegalScriptTagsCheck = async (composeState: ComposeState) => {
  if (composeState.sendOption.type === 'test') return true
  const toStudents =
    composeState.recipientsInfo.toStudents ||
    composeState.recipientsInfo.individuals.some((i) => i.guardianID === undefined)
  for (const op of composeState.bodyDelta!.ops) {
    if (op.insert && typeof op.insert !== 'string' && op.insert['message-script']) {
      const tag = typeof op.insert['message-script'] === 'string' ? (op.insert['message-script'] as string) : ''
      if (composeState.sendTypes.has('mass')) {
        await Popups.simplePopup(
          'You cannot send broadcast messages with [xxxx.xxxx] tags. Please remove these tags and try again.'
        )
        return false
      } else if (toStudents && tag.includes('guardian')) {
        await Popups.simplePopup(
          'You cannot send messages with [guardian.xxxx] tags unless this message is to guardians and not to any students. Please remove these tags and try again.'
        )
        return false
      }
    }
  }
  return true
}

const composeLargeMessageWarning = async (composeState: ComposeState, previewState: PreviewState) => {
  if (composeState.sendOption.type == 'draft' || !previewState || (previewState.count || 50) < 50) return true
  return await largeMessageWarning(composeState.sendTypes.has('text'), previewState.count)
}

export const largeMessageWarning = async (includesText: boolean, count?: number) => {
  if (count && count < 50) return true
  if (includesText) {
    return !!(await Popups.simpleConfirm(
      <b>Are you sure you want to message {count || 'this many'} people?</b>,
      includesText ? (
        <>
          Texting is best for urgent messages—overuse may reduce attention to future texts. If it's not urgent, consider
          email.
          <br /> Those without an email on file will still receive the message as a text. <br />
          As always, each recipient will get this <i>individually</i>.
        </>
      ) : undefined
    ))
  } else {
    return !!(await Popups.simpleConfirm(
      <b>Are you sure you want to message {count || 'this many'} people?</b>,
      <>
        As always, each recipient will get this <i>individually</i>.
      </>
    ))
  }
}

const confirmReceiptWarning = async (composeState: ComposeState) => {
  if (composeState.sendOption.type !== 'confirm') return true
  return !!(await Popups.confirm({
    title: 'Are you sure you want to enable confirm receipt?',
    message: (
      <>
        <b>This is an extra step for the recipient.</b>
        <br />
        They will have to click a link to view the message.
      </>
    ),
    yesText: 'Send',
    noText: 'Cancel',
  }))
}

export type PostableMessage = ReturnType<typeof composeStateToPostableMessage>

export const composeStateToPostableMessage = (cs: ComposeState, previewing?: boolean) => {
  const type =
    cs.sendOption.type === 'test' && !previewing
      ? 'test'
      : cs.sendTypes.has('mass')
        ? 'mass'
        : cs.sendTypes.has('email') && cs.sendTypes.has('text')
          ? 'both'
          : [...cs.sendTypes][0]

  const body = prepareDeltaToPost(cs.bodyDelta!)

  const recipients =
    cs.selectedRecipients.length === 1 &&
    !cs.selectedRecipients[0].group &&
    // check that the recipientInfo is not empty (otherwise this is from a student/guardian)
    (cs.recipientsInfo.individuals.length > 0 || cs.recipientsInfo.toSharedTeachers)
      ? cs.recipientsInfo.individuals.length > 0
        ? cs.recipientsInfo.individuals.map((r) => ({
            parent_id: r.guardianID,
            student_id: r.studentID,
          }))
        : [{ student_id: cs.selectedRecipients[0].id, teacherOnly: true }] // stupid fix for teacher only messages
      : {
          courses: cs.selectedRecipients.filter((r) => !!r.group).map((r) => r.groupID),
          people: cs.selectedRecipients.filter((r) => !r.group).map((r) => r.id),
          toParents: cs.recipientsInfo.toGuardians,
          toStudents: cs.recipientsInfo.toStudents,
          regardingStudent: cs.regardingStudent?.id,
        }

  const showSubject = (!cs.sendTypes.has('mass') || cs.sendTypes.size === 2) && cs.sendTypes.has('email')

  // TODO THIS PROBABLY SHOULDNT BE DEALING WITH THE GROSS MESSAGE FORMAT ON THIS END
  return {
    type,
    body,
    subject: showSubject ? cs.subject : undefined,
    recipients,
    sharedTeachers: cs.recipientsInfo.toSharedTeachers
      ? cs.recipientsInfo.sharedTeacherRecipients.map((r) => (r.group ? r.groupID : r.id))
      : [],
    attachments: cs.sendTypes.has('phone') ? [] : cs.attachments.map((c) => c.fileName),
    draft: cs.sendOption.type === 'draft',
    draftID: cs.draftID,
    confirmReceipt: cs.sendOption.type === 'confirm',
    allowRepliesToShared: !cs.sendTypes.has('mass') && cs.sharedTeachersSeeReplies,
    allow_replies: cs.massAllowReplying,
    massSendTypes: {
      email: cs.sendTypes.has('email'),
      text: cs.sendTypes.has('text'),
      phone: cs.sendTypes.has('phone'),
    },
    recording: cs.sendTypes.has('phone') && cs.sendTypes.has('mass') ? cs.recording?.serverID : undefined,
    recordingURL: cs.sendTypes.has('phone') && cs.sendTypes.has('mass') ? cs.recording?.audioUrl : undefined,
    test_types:
      cs.sendOption.type === 'test'
        ? {
            email: cs.sendTypes.has('email'),
            text: cs.sendTypes.has('text'),
            phone: cs.sendTypes.has('phone'),
            mass: cs.sendTypes.has('mass'),
          }
        : undefined,
    email: cs.testUser?.email,
    phoneNumber: cs.testUser?.phoneNumber,
    language: cs.testUser?.language,
    message: cs.sendOption.type === 'test' ? body : undefined,
    time: cs.sendOption.type === 'later' && !previewing ? cs.sendLaterDate!.toISOString() : undefined,
    emergency: cs.sendTypes.has('mass') ? cs.emergency : undefined,
  }
}

export const composeStateToCallMessage = (composeState: ComposeState) => {
  assert(
    composeState.sendOption.type === 'call-interpreted' ||
      composeState.sendOption.type === 'schedule-interpreted' ||
      composeState.sendOption.type === 'schedule-video',
    'invalid call send option'
  )
  // THIS IS NOT WORKING IF YOU PRESS ONE BUTTON AND THEN THE OTHER, CAUSE COMPOSE STATE ISNT CLEARED
  // assert(
  //   (composeState.sendOption.type === 'schedule-video') === (composeState.lexikeetCallLanguage === undefined),
  //   'must have language only for scheduled video (non lk) call'
  // )
  // assert(
  //   (!!composeState.scheduledTimes && composeState.scheduledTimes.length > 0) ===
  //     (composeState.sendOption.type !== 'call-interpreted'),
  //   'need times for scheduling'
  // )
  // assert(
  //   !!composeState.callSuggestingTimes === (composeState.sendOption.type !== 'call-interpreted'),
  //   'only can suggest for scheduled'
  // )

  const individual = composeState.recipientsInfo.individuals[0]
  const time = composeState.callSuggestingTimes
    ? composeState.scheduledTimes!.map((t) => t.toISOString())
    : composeState.scheduledTimes && composeState.scheduledTimes.length > 0
      ? composeState.scheduledTimes[0].toISOString()
      : undefined
  return {
    student_id: individual.studentID,
    parent_id: individual.guardianID,
    sharedTeachers: composeState.recipientsInfo.sharedTeacherRecipients.map((r) => (r.group ? r.groupID : r.id)),
    otherFamilyMembers: composeState.recipientsInfo.extraInvites.map((e) => ({
      student_id: e.studentID,
      parent_id: e.guardianID,
    })),
    language: composeState.sendOption.type.includes('interpreted')
      ? composeState.lexikeetCallLanguage!.code
      : undefined,
    time: composeState.sendOption.type.includes('schedule') ? time : undefined,
    note: composeState.sendOption.type.includes('schedule') ? composeState.scheduledTimeNote : undefined,
    english: composeState.sendOption.type === 'schedule-video',
    lexikeetInPerson: composeState.sendOption.forLexikeet ? composeState.lexikeetInPerson : undefined,
  }
}

type MessageResponse =
  | number // id of the message
  | 'invalid-email'
  | 'invalid-phone' //errors for test messages
  // call responses
  | string // authorize message
  | undefined // sometimes returns nothing?
  | any[] // could return some garbage for gcal invites that we done use

export const sendComposedMessage = (composeState: ComposeState) => {
  if (composeState.sendOption.type === 'call') {
    throw new Error('call should be handled elsewhere')
  } else if (
    composeState.sendOption.type === 'call-interpreted' ||
    composeState.sendOption.type === 'schedule-interpreted' ||
    composeState.sendOption.type === 'schedule-video'
  ) {
    return makeRequest<MessageResponse>('/frontend_api/makeCall', composeStateToCallMessage(composeState)).then((r) =>
      makeCallResponseChecks(composeState, r)
    )
  }
  return makeRequest<MessageResponse>('/frontend_api/sendMessage', composeStateToPostableMessage(composeState))
}

const makeCallResponseChecks = (message: ComposeState, response: MessageResponse) => {
  if (typeof response === 'string' && response.includes('AUTHORIZE:')) {
    const link = response.replace('AUTHORIZE:', '')
    window.open(link, '_blank')
    Toasts.showToast({
      message: (
        <>
          You must authorize us to make video calls on your account before continuing. Please complete the task{' '}
          <a href={link} target="_blank">
            here
          </a>{' '}
          and try again.
        </>
      ),
      duration: 1000000,
      error: true,
    })
    throw new AxiosError(REQUEST_ABORTED) // dont close compose
  }
  return response
}

export const messageSuccessNotifications = (
  user: User,
  message: ComposeState,
  response: MessageResponse,
  showLatestMessagePreview: () => void
) => {
  if (message.sendOption.type === 'schedule-interpreted' || message.sendOption.type === 'schedule-video') {
    if (message.callSuggestingTimes) {
      Toasts.showToast({ message: 'Your call has been requested!' })
    } else {
      Toasts.showToast({ message: 'Your call has been scheduled!' })
    }
  } else if (message.sendOption.type === 'call-interpreted') {
    if (typeof response !== 'string') {
      Toasts.showToast({ message: 'Error making call', error: true })
    } else {
      Popups.simplePopup(
        <>
          If your call did not automatically open in a new tab, click{' '}
          <a target="_blank" href={response}>
            here
          </a>
        </>
      )
      window.open(response, '_blank')
    }
  } else if (message.sendOption.type === 'draft') {
    Toasts.showToast({ message: 'Your draft has been saved!' })
  } else if (message.sendOption.type === 'later') {
    Toasts.showToast({ message: 'Your message has been scheduled!' })
  } else if (user.type === 'teacher') {
    Toasts.showToast({
      subject: 'Your message has been sent!',
      message: (
        <>
          <span className="cursor-pointer underline" onClick={showLatestMessagePreview}>
            Click here
          </span>{' '}
          to see what is looks like.
        </>
      ),
      closeOnClick: true,
    })
  } else {
    Toasts.showToast({
      message: 'Your message has been sent!',
    })
  }
}
